import { featureEach } from '@turf/meta';
import unkinkPolygon from '@turf/unkink-polygon';
import cx from 'classnames';
import AsyncSuspense, { queryResultToAsyncState } from "../../core/components/AsyncSuspense/AsyncSuspense";
import Modal from "../../core/components/Modal/Modal";
import ModalActionBar from "../../core/components/Modal/ModalActionBar";
import ModalBody from "../../core/components/Modal/ModalBody";
import ModalHeader from "../../core/components/Modal/ModalHeader";
import { makeUnkinkable } from "../../core/makeUnkinkable";
import { LimitType, useLimitSchemasQuery, useMeQuery } from 'legoland-sdk/dist/experimental';
import { Preloader } from 'llui';
import { useEffectiveLimitsQuery } from "../../products/api/limitsApiHooks";
import { ProductLogo } from "../../products/components/ProductLogo";
import GeoLimitEditor from "../../products/components/limit-editors/GeoLimitEditor";
import { useEffect, useState } from 'react';
import { Button, useToasts } from 'tombac';
import CountLimitUsageViewer from "./limit-viewers/CountLimitUsageViewer";
import DateRangeLimitViewer from "./limit-viewers/DateRangeLimitViewer";
import GeoLimitViewer from "./limit-viewers/GeoLimitViewer";
import "./LimitUsageModal.css";
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const GeoLimitMap = _ref => {
  let {
    limit,
    schema
  } = _ref;
  return /*#__PURE__*/_jsx(GeoLimitEditor.ExtendedEditor, {
    defaultLimit: null,
    limit: limit,
    onChange: () => {},
    readOnly: true,
    schema: schema,
    "data-sentry-element": "unknown",
    "data-sentry-component": "GeoLimitMap",
    "data-sentry-source-file": "LimitUsageModal.tsx"
  });
};
function getViewers(_ref2, schema) {
  let {
    limits,
    sharedWith,
    usage
  } = _ref2;
  const viewerTypeMap = {
    [LimitType.Count]: [CountLimitUsageViewer],
    [LimitType.DateRange]: [DateRangeLimitViewer],
    [LimitType.Km]: [CountLimitUsageViewer, {
      availableAsPercentage: true,
      unit: 'km'
    }],
    [LimitType.SquareKm]: [CountLimitUsageViewer, {
      availableAsPercentage: true,
      unit: 'km²'
    }]
  };
  const viewers = schema.filter(_ref3 => {
    let {
      customizable,
      name,
      type,
      visible
    } = _ref3;
    return customizable && visible && viewerTypeMap[type] && Object.hasOwn(limits, name);
  }).map(schemaItem => {
    const [Viewer, props = {}] = viewerTypeMap[schemaItem.type];
    return /*#__PURE__*/_jsx(Viewer, {
      label: schemaItem.label,
      limit: limits[schemaItem.name],
      sharedWith: sharedWith.find(_ref4 => {
        let {
          names
        } = _ref4;
        return names.includes(schemaItem.name);
      })?.productId,
      used: Object.hasOwn(usage, schemaItem.name) ? usage[schemaItem.name] : undefined,
      ...props
    }, schemaItem.name);
  }).filter(Boolean);
  return viewers;
}
const EmptyGeoLimitContextProvider = _ref5 => {
  let {
    children
  } = _ref5;
  return /*#__PURE__*/_jsx(_Fragment, {
    children: children
  });
};
const LimitUsageModal = _ref6 => {
  let {
    className,
    isOpen,
    onRequestClose,
    onHasLimits,
    product,
    ...modalProps
  } = _ref6;
  if (product.access.status !== 'GRANTED') throw new Error('Limit usage modal can be used only for products with granted access.');
  const {
    addToast
  } = useToasts();
  const meQuery = useMeQuery();
  const schemasQuery = useLimitSchemasQuery({
    productId: product.id
  });
  const schemas = schemasQuery.data?.schemas;
  const schemaState = queryResultToAsyncState(schemasQuery, schemasQuery.data?.schemas);
  const effectiveLimitsQuery = useEffectiveLimitsQuery({
    productId: product.id,
    username: meQuery.data.me.email
  });
  const {
    limits,
    sharedWith,
    usage
  } = effectiveLimitsQuery.data?.result ?? {};
  const effectiveLimitsState = queryResultToAsyncState(effectiveLimitsQuery, effectiveLimitsQuery.data?.result);
  useEffect(() => {
    if (isOpen) {
      schemasQuery.refetch();
      effectiveLimitsQuery.refetch();
    }
  }, [isOpen]);
  const dataStates = [schemaState, effectiveLimitsState];
  const geoLimitSchema = schemas?.find(item => item.type === LimitType.Geo);
  const [geoLimit, setGeoLimit] = useState(null);
  useEffect(() => {
    if (geoLimitSchema && limits && limits[geoLimitSchema.name]) {
      try {
        const geoLimit = unkinkPolygon(makeUnkinkable(limits[geoLimitSchema.name]));
        let featureNum = 1;
        featureEach(geoLimit, feature => {
          feature.id = featureNum;
          feature.properties = {
            featureName: `Region ${featureNum}`
          };
          featureNum++;
        });
        setGeoLimit(geoLimit);
      } catch (error) {
        addToast('Failed to decode geographical limit', 'danger', {
          content: error?.message ?? error
        });
        console.error(error);
      }
    }
  }, [geoLimitSchema, limits]);
  const GeoLimitContextProvider = geoLimit ? GeoLimitEditor.ContextProvider : EmptyGeoLimitContextProvider;
  const viewers = limits && usage && schemas ? getViewers({
    limits,
    sharedWith,
    usage
  }, schemas) : [];
  useEffect(() => {
    if (limits && usage && schemas && viewers.length > 0 || geoLimit) onHasLimits();
  }, [geoLimit, limits, sharedWith, usage, schemas]);
  return /*#__PURE__*/_jsx(GeoLimitContextProvider, {
    "data-sentry-element": "GeoLimitContextProvider",
    "data-sentry-component": "LimitUsageModal",
    "data-sentry-source-file": "LimitUsageModal.tsx",
    children: /*#__PURE__*/_jsxs(Modal, {
      aside: geoLimit && /*#__PURE__*/_jsx(AsyncSuspense, {
        state: dataStates,
        children: () => /*#__PURE__*/_jsx(GeoLimitMap, {
          limit: geoLimit,
          schema: geoLimitSchema
        })
      }),
      className: cx('LimitUsageModal', className),
      isOpen: isOpen,
      onRequestClose: onRequestClose,
      size: geoLimit ? 'xl' : 'auto',
      ...modalProps,
      "data-sentry-element": "Modal",
      "data-sentry-source-file": "LimitUsageModal.tsx",
      "data-sentry-element": "Modal",
      children: [/*#__PURE__*/_jsx(ModalHeader, {
        image: /*#__PURE__*/_jsx(ProductLogo, {
          productId: product.id
        }),
        primaryTitle: "Usage limits",
        secondaryTitle: product.displayName,
        "data-sentry-element": "ModalHeader",
        "data-sentry-source-file": "LimitUsageModal.tsx",
        "data-sentry-element": "ModalHeader"
      }), /*#__PURE__*/_jsx(ModalBody, {
        "data-sentry-element": "ModalBody",
        "data-sentry-source-file": "LimitUsageModal.tsx",
        "data-sentry-element": "ModalBody",
        children: /*#__PURE__*/_jsx(AsyncSuspense, {
          pending: /*#__PURE__*/_jsx(Preloader, {}),
          state: dataStates,
          "data-sentry-element": "AsyncSuspense",
          "data-sentry-source-file": "LimitUsageModal.tsx",
          "data-sentry-element": "AsyncSuspense",
          children: () => /*#__PURE__*/_jsxs(_Fragment, {
            children: [viewers.length ? viewers : /*#__PURE__*/_jsx("p", {
              children: "No limits are set."
            }), geoLimit && /*#__PURE__*/_jsx(GeoLimitViewer, {
              label: geoLimitSchema.label,
              limit: geoLimit
            })]
          })
        })
      }), /*#__PURE__*/_jsx(ModalActionBar, {
        "data-sentry-element": "ModalActionBar",
        "data-sentry-source-file": "LimitUsageModal.tsx",
        "data-sentry-element": "ModalActionBar",
        children: /*#__PURE__*/_jsx(Button, {
          onClick: onRequestClose,
          variant: "flat",
          "data-sentry-element": "Button",
          "data-sentry-source-file": "LimitUsageModal.tsx",
          "data-sentry-element": "Button",
          children: "Close"
        })
      })]
    })
  });
};
export default LimitUsageModal;